import { Navigate } from "react-router";

import { ConfigViewSwitch } from "@/ui/files/ConfigViewSwitch";

import {
  CLINICAL_CONFIG_SECTIONS,
  CLINICAL_DEFAULT_SLUG,
} from "@/shell/navModel";

import { useProjectId } from "./ProjectLayoutRoute";
import { SectionSurface } from "./SectionSurface";

export function ProjectClinicalConfigRoute() {
  const projectId = useProjectId();
  return (
    <>
      <ConfigViewSwitch projectId={projectId} active="guided" />
      <SectionSurface
        groups={CLINICAL_CONFIG_SECTIONS}
        basePath={`/projects/${encodeURIComponent(projectId)}/config`}
        railLabel="Clinical configuration sections"
        defaultSlug={CLINICAL_DEFAULT_SLUG}
        emptyHeading="Clinical configuration adopts versioned library assets into this project's overlay."
        // The only surface whose slugs are members of the contract's
        // `SectionSlug`, so the only one that may fetch the effective projection
        // and render #7's diagnostics against it.
        projectId={projectId}
        surface="clinical"
      />
    </>
  );
}

/**
 * Legacy `/product-settings/**` addresses, which REDIRECT to the PROJECT ROOT
 * (#516).
 *
 * #153 removed Product Settings from the Clinician Editor — those values are
 * Tenant Manifest domain, owned outside this application — and left an
 * `EmptyState` behind that said so, on the reasoning that a stale bookmark
 * should land on a statement rather than a bare 404. #516's Definition of Done
 * replaces that statement with the canonical redirect and no rendered
 * explanatory state.
 *
 * ## Why the ROOT and not BG sets
 *
 * The same Definition-of-Done sentence says two different things: "obsolete
 * product-setting/config paths redirect to BG sets" and "exactly as canonical".
 * They are not the same address, so one of them has to give, and the ticket
 * settles which: its Scope permits "exactly the visible post-login product in
 * the canonical authority", and its first Definition-of-Done bullet requires
 * matching canonical evidence. The canonical authority governs its own literal
 * wording.
 *
 * This shipped as BG sets first, on the literal words, with the target recorded
 * as an unverified assumption. Manual QA then measured the reference directly:
 * `/projects/mod/product-settings`, `…/branding`, `…/orb-avatar`,
 * `…/branding/logo`, `…/product-settings/` and `/projects/sheba/product-settings`
 * ALL land on `/projects/<id>` — the project root, which renders the Overview.
 *
 * It also proved the rule is deliberate rather than an artifact of prefix
 * matching, which is the measurement that makes this safe to follow: in the same
 * reference `/projects/mod/zzz`, `/product-settings-x`, `/settings`, `/overview`,
 * `/adoptions` and `/config/not-a-section` all render Not Found and do NOT
 * redirect. Only `product-settings` redirects, and its target is the root.
 *
 * The bare `/projects/:id/config` redirect is a DIFFERENT rule and is unchanged:
 * it still resolves in place to `config/bg-sets`, because a section surface
 * reached without a section has a default section, not a missing address.
 *
 * `replace`, not a push: the legacy address must not sit in the clinician's
 * history where Back would return them to a redirect that fires again.
 */
export function ProductSettingsRedirectRoute() {
  const projectId = useProjectId();
  return <Navigate to={`/projects/${encodeURIComponent(projectId)}`} replace />;
}
